<template>
  <div
    class="dij-ptb"
    :class="{
      'dij-ptb--done': completed,
      'dij-ptb--hidden': !demo && value === null,
    }"
  >
    <div class="dij-ptb__track">
      <div class="dij-ptb__fill" :style="{ width: progress + '%' }">
        <span class="dij-ptb__shimmer" />
        <span class="dij-ptb__pulse" v-if="progress > 0 && progress < 100" />
      </div>
      <span class="dij-ptb__label">
        <span v-if="message" class="dij-ptb__message">{{ message }}</span>
        {{ Math.round(progress) }}%
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProgressTaskBar',

  props: {
    value: {
      type: Number,
      default: null,
    },
    message: {
      type: String,
      default: 'Overall Progress',
    },
    demo: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      progress: 0,
      completed: false,
      _timer: null,
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val !== null) {
          this.stopDemo();
          if (val >= 100) {
            this.progress = 100;
            this.completed = true;
          } else {
            this.completed = false;
            this.progress = Math.min(100, Math.max(0, val));
          }
        }
      },
    },
  },

  mounted() {
    if (this.demo) {
      this.runDemo();
    }
  },

  beforeDestroy() {
    this.stopDemo();
  },

  methods: {
    runDemo() {
      this.progress = 0;
      this.completed = false;
      this._timer = setInterval(() => {
        if (this.progress >= 100) {
          this.stopDemo();
          this.completed = true;
          // Restart after the fade-out animation finishes (800ms)
          setTimeout(() => {
            this.runDemo();
          }, 1200);
          return;
        }
        this.progress = Math.min(100, this.progress + 1);
      }, 40);
    },
    stopDemo() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    },
  },
};
</script>

<style scoped>
.dij-ptb {
  position: fixed;
  top: 50px; /* sits flush below the navbar */
  left: 0;
  width: 100%;
  height: 18px;
  background: transparent;
  z-index: 999;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
  transition: opacity 0.4s ease;
}

.dij-ptb--done {
  opacity: 0;
  pointer-events: none;
}

.dij-ptb--hidden {
  opacity: 0;
  pointer-events: none;
}

.dij-ptb__track {
  position: relative;
  width: 100%;
  height: 100%;
  background: rgba(104, 37, 142, 0.1);
  border-radius: 0;
  overflow: hidden;
  backdrop-filter: blur(4px);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.15);
}

.dij-ptb__fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 0;
  background: linear-gradient(
    90deg,
    rgba(104, 37, 142, 0.6) 0%,
    rgba(155, 59, 196, 0.55) 40%,
    rgba(196, 94, 168, 0.5) 75%,
    rgba(224, 127, 204, 0.45) 100%
  );
  box-shadow: 0 0 12px rgba(104, 37, 142, 0.6),
    0 0 28px rgba(196, 94, 168, 0.35);
  transition: width 0.1s linear;
  overflow: hidden;
}

/* Animated shimmer sweep */
.dij-ptb__shimmer {
  position: absolute;
  top: 0;
  left: -60%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    120deg,
    transparent 20%,
    rgba(255, 255, 255, 0.35) 50%,
    transparent 80%
  );
  animation: shimmer 1.8s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    left: -60%;
  }
  100% {
    left: 120%;
  }
}

/* Pulse dot at the leading edge */
.dij-ptb__pulse {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.6);
  animation: pulse-ring 1.2s ease-out infinite;
}

@keyframes pulse-ring {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

/* Centered label */
.dij-ptb__label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 0 12px rgba(104, 37, 142, 0.8), 0 1px 3px rgba(0, 0, 0, 0.6);
  letter-spacing: 0.08em;
  white-space: nowrap;
  pointer-events: none;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dij-ptb__message {
  font-weight: 400;
  opacity: 0.9;
  letter-spacing: 0.04em;
  border-right: 1px solid rgba(255, 255, 255, 0.35);
  padding-right: 8px;
}
</style>
